import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Pagination,
  Stack,
  Menu,
  ListItemText,
  ListItemIcon
} from '@mui/material'
import { Add as AddIcon, KeyboardArrowDown } from '@mui/icons-material'
import { ArrowUpward, ArrowDownward } from '@mui/icons-material'
import { useAppSelector } from '@/hooks/redux'
import { apiService } from '@/services/api'
import AdminsTab from '@/components/AdminsTab'

interface Country {
  code: string
  name: string
  dialCode: string
  flag: string
}

const countries: Country[] = [
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263', flag: '🇿🇼' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'BW', name: 'Botswana', dialCode: '+267', flag: '🇧🇼' },
  { code: 'ZM', name: 'Zambia', dialCode: '+260', flag: '🇿🇲' },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258', flag: '🇲🇿' },
  { code: 'MW', name: 'Malawi', dialCode: '+265', flag: '🇲🇼' },
  { code: 'NA', name: 'Namibia', dialCode: '+264', flag: '🇳🇦' },
  { code: 'TZ', name: 'Tanzania', dialCode: '+255', flag: '🇹🇿' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
  { code: 'UG', name: 'Uganda', dialCode: '+256', flag: '🇺🇬' },
  { code: 'RW', name: 'Rwanda', dialCode: '+250', flag: '🇷🇼' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', flag: '🇪🇹' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' }
]

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  phone: string
  role: string
  status?: 'pending' | 'approved' | 'rejected' | 'suspended'
  email_verified: boolean
  phone_verified: boolean
  created_at: string
  deleted_at?: string
  deletion_reason?: string
  is_deleted?: boolean
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [actionDialog, setActionDialog] = useState<'approve' | 'reject' | 'suspend' | 'activate' | 'delete' | 'permanent_delete' | null>(null)
  const [notes, setNotes] = useState('')
  const [currentTab, setCurrentTab] = useState(0)
  const [createAdminDialog, setCreateAdminDialog] = useState(false)
  const [createSuperAdminDialog, setCreateSuperAdminDialog] = useState(false)
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    role: 'admin',
    centre_id: ''
  })
  const [centres, setCentres] = useState<any[]>([])
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0])
  const [phoneLocal, setPhoneLocal] = useState('')
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null)
  const [countrySearchQuery, setCountrySearchQuery] = useState('')
  const [createLoading, setCreateLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const { token, user } = useAppSelector(state => state.auth)

  // Pagination and filter state
  const [page, setPage] = useState(1)
  const [limit] = useState(50) // Show more users per page to avoid pagination confusion
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [roleFilter, setRoleFilter] = useState<string>('')

  // Sorting state for deleted users
  const [sortField, setSortField] = useState<string>('deleted_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Status filters - add 'admins' for superadmin
  const getStatusFilters = () => {
    if (user?.role === 'superadmin') {
      return ['pending', 'approved', 'rejected', 'suspended', 'deleted', 'admins']
    }
    return ['pending', 'approved', 'rejected', 'suspended', 'deleted']
  }
  
  const statusFilters = getStatusFilters()
  
  // Role options based on user role and impersonation state
  const getRoleOptions = () => {
    if (user?.role === 'superadmin') {
      if (user?.is_impersonating) {
        // When impersonating a center, show center-specific roles like admin
        return ['farmer', 'advisor', 'admin']
      } else {
        // When not impersonating (global view), show all global roles
        return ['buyer', 'superadmin', 'admin']
      }
    } else {
      return ['farmer', 'advisor', 'admin']
    }
  }

  const roleOptions = getRoleOptions()

  // Fetch centres for admin creation
  const fetchCentres = async () => {
    try {
      const response = await apiService.getCentres()
      setCentres(Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
      console.error('Failed to fetch centres:', err)
    }
  }

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchCentres()
    }
  }, [user])

  const fetchUsers = async (status?: string, currentPage = page, currentRoleFilter = roleFilter) => {
    setLoading(true)
    setError('')

    try {
      const filters: { status?: string; role?: string; page?: number; limit?: number; centre_id?: string } = {
        page: currentPage,
        limit: limit
      }

      if (status && status !== 'deleted') {
        filters.status = status
      }

      // Apply role filter based on user role and impersonation state
      if (user?.role === 'superadmin') {
        if (user?.is_impersonating) {
          // When impersonating a center, show center-specific users (farmers, advisors, admins)
          if (user?.centre_id) {
            filters.centre_id = user.centre_id
          }
          if (currentRoleFilter) {
            filters.role = currentRoleFilter
          } else {
            // No default filter - show all center users
          }
        } else {
          // When not impersonating (global view), show global users (buyers, superadmins, admins)
          filters.role = currentRoleFilter || 'buyer'
        }
      } else {
        // Admin sees farmers and advisors (unless specific role filter is applied)
        if (currentRoleFilter) {
          filters.role = currentRoleFilter
        } else {
          // No default filter for admin - they see all farmers and advisors
        }
      }

      let response
      if (status === 'deleted') {
        response = await apiService.getDeletedUsers(currentPage, limit)
        setUsers(Array.isArray(response.data) ? response.data : [])
        // Handle pagination from response if available
        if (response.pagination) {
          setTotalUsers(response.pagination.total || 0)
          setTotalPages(response.pagination.totalPages || 1)
        } else {
          // Fallback for when pagination info isn't in response
          setTotalUsers(Array.isArray(response.data) ? response.data.length : 0)
          setTotalPages(Math.ceil((Array.isArray(response.data) ? response.data.length : 0) / limit))
        }
      } else {
        response = await apiService.getUsers(filters)
        // Filter out deleted users from active user lists (pending, approved, rejected, suspended)
        const allUsers = Array.isArray(response.data) ? response.data : []
        const activeUsers = allUsers.filter(user => !user.is_deleted)
        setUsers(activeUsers)
        setTotalUsers(response.pagination?.total ?? activeUsers.length)
        setTotalPages(response.pagination?.totalPages ?? Math.ceil(activeUsers.length / limit))
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (token && currentTab < statusFilters.length - 1) {
      // Don't fetch for 'admins' tab - it has its own component
      const status = statusFilters[currentTab]
      if (status !== 'admins') {
        fetchUsers(status)
        setPage(1) // Reset to first page when tab changes
      }
    }
  }, [token, currentTab])

  useEffect(() => {
    if (token && currentTab >= 0) {
      const status = statusFilters[currentTab]
      if (status !== 'admins') {
        fetchUsers(status, page, roleFilter)
      }
    }
  }, [page, roleFilter])

  // Re-sort when sort parameters change
  useEffect(() => {
    if (currentTab === 4 && sortField === 'deleted_at') {
      console.log('Sort parameters changed, re-sorting users')
      // Force a re-render by updating users state with sorted version
      setUsers(prevUsers => getSortedUsers([...prevUsers]))
    }
  }, [sortField, sortDirection, currentTab])

  const handleTabChange = (_: any, newValue: number) => {
    setCurrentTab(newValue)
    setRoleFilter('') // Reset role filter when changing tabs
    setPage(1) // Reset to first page when changing tabs
    setSortField('deleted_at') // Reset sort field
    setSortDirection('desc') // Reset sort direction
  }

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage + 1) // Material-UI Pagination is 0-indexed, but our API is 1-indexed
  }

  const handleRoleFilterChange = (event: any) => {
    setRoleFilter(event.target.value)
    setPage(1) // Reset to first page when filter changes
  }

  const handleAction = async (action: 'approve' | 'reject' | 'suspend' | 'activate' | 'delete' | 'permanent_delete') => {
    if (!selectedUser) return

    try {
      if (action === 'approve') {
        await apiService.approveUser(selectedUser.id, notes)
      } else if (action === 'reject') {
        await apiService.rejectUser(selectedUser.id, notes)
      } else if (action === 'suspend') {
        await apiService.suspendUser(selectedUser.id, notes)
      } else if (action === 'activate') {
        await apiService.activateUser(selectedUser.id, notes)
      } else if (action === 'delete') {
        await apiService.softDeleteUser(selectedUser.id, notes)
      } else if (action === 'permanent_delete') {
        await apiService.permanentlyDeleteUser(selectedUser.id)
      }
      
      // Refresh the list
      const status = statusFilters[currentTab]
      fetchUsers(status)
      
      // Close dialog
      setActionDialog(null)
      setSelectedUser(null)
      setNotes('')
    } catch (err: any) {
      setError(err.message || `Failed to ${action} user`)
    }
  }

  const handleCreateAdmin = async () => {
    setCreateLoading(true)
    setError('')
    setSuccessMessage('')
    
    try {
      const adminData = {
        email: newAdmin.email,
        first_name: newAdmin.first_name,
        last_name: newAdmin.last_name,
        phone: newAdmin.phone,
        password: newAdmin.password,
        role: 'admin',
        centre_id: newAdmin.centre_id
      }
      
      await apiService.createAdmin(adminData)
      
      setSuccessMessage(`Admin user ${newAdmin.first_name} ${newAdmin.last_name} created successfully!`)
      
      // Refresh the list
      const status = statusFilters[currentTab]
      if (status !== 'admins') {
        fetchUsers(status)
      }
      
      // Close dialog and reset form
      setCreateAdminDialog(false)
      setNewAdmin({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        password: '',
        role: 'admin',
        centre_id: ''
      })
      setPhoneLocal('')
      setSelectedCountry(countries[0])
    } catch (err: any) {
      setError(err.message || 'Failed to create admin user')
    } finally {
      setCreateLoading(false)
    }
  }

  const handleCreateSuperAdmin = async () => {
    setCreateLoading(true)
    setError('')
    setSuccessMessage('')
    
    try {
      const adminData = {
        email: newAdmin.email,
        first_name: newAdmin.first_name,
        last_name: newAdmin.last_name,
        phone: newAdmin.phone,
        password: newAdmin.password,
        role: 'superadmin'
      }
      
      await apiService.createAdmin(adminData)
      
      setSuccessMessage(`Super Admin user ${newAdmin.first_name} ${newAdmin.last_name} created successfully!`)
      
      // Refresh the list
      const status = statusFilters[currentTab]
      if (status !== 'admins') {
        fetchUsers(status)
      }
      
      // Close dialog and reset form
      setCreateSuperAdminDialog(false)
      setNewAdmin({
        email: '',
        first_name: '',
        last_name: '',
        phone: '',
        password: '',
        role: 'admin',
        centre_id: ''
      })
      setPhoneLocal('')
      setSelectedCountry(countries[0])
    } catch (err: any) {
      setError(err.message || 'Failed to create super admin user')
    } finally {
      setCreateLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success'
      case 'rejected': return 'error'
      case 'pending': return 'warning'
      case 'suspended': return 'error'
      default: return 'default'
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const handleSort = (field: string) => {
    if (currentTab !== 4) return // Only allow sorting on deleted tab (index 4)
    
    console.log('Sorting triggered:', field, 'Current direction:', sortDirection)
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const getSortedUsers = (users: User[]) => {
    if (currentTab !== 4 || sortField !== 'deleted_at') {
      console.log('Not sorting:', currentTab, sortField)
      return users
    }
    
    console.log('Sorting users by deleted_at:', sortDirection)
    return [...users].sort((a, b) => {
      const dateA = new Date(a.deleted_at || '1970-01-01').getTime()
      const dateB = new Date(b.deleted_at || '1970-01-01').getTime()
      
      if (sortDirection === 'asc') {
        return dateA - dateB
      } else {
        return dateB - dateA
      }
    })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Users Management
        </Typography>
        <Box display="flex" gap={1}>
          {user?.role === 'superadmin' && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setCreateAdminDialog(true)}
              >
                Create Admin
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setCreateSuperAdminDialog(true)}
              >
                Create Super Admin
              </Button>
            </>
          )}
          {user?.role === 'admin' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateAdminDialog(true)}
            >
              Create Admin
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Pending" />
        <Tab label="Approved" />
        <Tab label="Rejected" />
        <Tab label="Suspended" />
        <Tab label="Deleted" />
        {user?.role === 'superadmin' && <Tab label="Admins" />}
      </Tabs>

      {/* Show Admins Tab Content for Superadmin */}
      {currentTab === statusFilters.indexOf('admins') && user?.role === 'superadmin' ? (
        <AdminsTab />
      ) : (
        <>
      {/* Filters */}
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Filter by Role</InputLabel>
          <Select
            value={roleFilter}
            label="Filter by Role"
            onChange={handleRoleFilterChange}
          >
            <MenuItem value="">
              <em>All Roles</em>
            </MenuItem>
            {roleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {totalUsers > 0 && (
          <Typography variant="body2" color="text.secondary">
            Showing {users.length} of {totalUsers} users
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                {statusFilters[currentTab] !== 'deleted' && <TableCell>Status</TableCell>}
                <TableCell>Verified</TableCell>
                <TableCell 
                  sx={{ cursor: statusFilters[currentTab] === 'deleted' ? 'pointer' : 'default' }}
                  onClick={() => statusFilters[currentTab] === 'deleted' && handleSort('deleted_at')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {statusFilters[currentTab] === 'deleted' ? 'Deleted At' : 'Created'}
                    {statusFilters[currentTab] === 'deleted' && sortField === 'deleted_at' && (
                      sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                {statusFilters[currentTab] === 'deleted' && <TableCell>Reason</TableCell>}
                {statusFilters[currentTab] !== 'deleted' && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {getSortedUsers(users).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.phone}</TableCell>
                  <TableCell>
                    <Chip label={user.role} size="small" />
                  </TableCell>
                  {statusFilters[currentTab] !== 'deleted' && (
                    <TableCell>
                      <Chip 
                        label={user.status} 
                        color={getStatusColor(user.status!) as any}
                        size="small" 
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {user.email_verified && user.phone_verified ? '✅' : '❌'}
                  </TableCell>
                  <TableCell>{formatDateTime(user.is_deleted ? user.deleted_at! : user.created_at)}</TableCell>
                  {statusFilters[currentTab] === 'deleted' && (
                    <TableCell>{user.deletion_reason || 'No reason provided'}</TableCell>
                  )}
                  {statusFilters[currentTab] !== 'deleted' && (
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        {user.status === 'pending' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => {
                                setSelectedUser(user)
                                setActionDialog('approve')
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setSelectedUser(user)
                                setActionDialog('reject')
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        {user.status === 'approved' && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => {
                                setSelectedUser(user)
                                setActionDialog('suspend')
                              }}
                              sx={{ mr: 1 }}
                            >
                              Deactivate
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setSelectedUser(user)
                                setActionDialog('delete')
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                        {user.status === 'rejected' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => {
                              setSelectedUser(user)
                              setActionDialog('approve')
                            }}
                          >
                            Approve
                          </Button>
                        )}
                        {user.status === 'suspended' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => {
                                setSelectedUser(user)
                                setActionDialog('activate')
                              }}
                              sx={{ mr: 1 }}
                            >
                              Activate
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setSelectedUser(user)
                                setActionDialog('delete')
                              }}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                        {user.is_deleted && (
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setSelectedUser(user)
                              setActionDialog('permanent_delete')
                            }}
                          >
                            Permanent Delete
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={statusFilters[currentTab] === 'deleted' ? 8 : 8} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {totalUsers > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Stack spacing={2}>
            <Pagination
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              disabled={totalPages <= 1}
            />
            <Typography variant="body2" color="text.secondary" align="center">
              Page {page} of {Math.max(totalPages, 1)} ({totalUsers} total users)
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Action Dialog */}
      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)}>
        <DialogTitle>
          {actionDialog === 'approve' && 'Approve User'}
          {actionDialog === 'reject' && 'Reject User'}
          {actionDialog === 'suspend' && 'Suspend User'}
          {actionDialog === 'activate' && 'Activate User'}
          {actionDialog === 'delete' && 'Delete User'}
          {actionDialog === 'permanent_delete' && 'Permanently Delete User'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedUser && `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`}
          </Typography>
          {actionDialog === 'permanent_delete' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Warning:</strong> This action cannot be undone. All user data will be permanently removed from the system.
            </Alert>
          )}
          {actionDialog !== 'permanent_delete' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label={
                actionDialog === 'approve' ? 'Approval Notes (optional)' :
                actionDialog === 'reject' ? 'Rejection Reason (optional)' :
                actionDialog === 'suspend' ? 'Suspension Reason (optional)' :
                actionDialog === 'delete' ? 'Deletion Reason (optional)' :
                'Activation Notes (optional)'
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleAction(actionDialog!)}
            variant="contained"
            color={
              actionDialog === 'approve' || actionDialog === 'activate' ? 'success' : 
              actionDialog === 'suspend' ? 'warning' : 'error'
            }
          >
            {actionDialog === 'approve' && 'Approve'}
            {actionDialog === 'reject' && 'Reject'}
            {actionDialog === 'suspend' && 'Suspend'}
            {actionDialog === 'activate' && 'Activate'}
            {actionDialog === 'delete' && 'Delete'}
            {actionDialog === 'permanent_delete' && 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={createAdminDialog} onClose={() => setCreateAdminDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Admin User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newAdmin.first_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newAdmin.last_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Phone Number *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Country Code Dropdown */}
                <Button
                  variant="outlined"
                  onClick={(e) => setCountryMenuAnchor(e.currentTarget)}
                  sx={{ 
                    minWidth: 140, 
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    color: 'text.primary',
                    borderColor: 'divider'
                  }}
                  endIcon={<KeyboardArrowDown />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>{selectedCountry.flag}</span>
                    <span>{selectedCountry.dialCode}</span>
                  </Box>
                </Button>
                
                {/* Country Selection Menu */}
                <Menu
                  anchorEl={countryMenuAnchor}
                  open={Boolean(countryMenuAnchor)}
                  onClose={() => {
                    setCountryMenuAnchor(null)
                    setCountrySearchQuery('')
                  }}
                  PaperProps={{
                    sx: { width: 320, maxHeight: 400 }
                  }}
                >
                  <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search country or code..."
                      value={countrySearchQuery}
                      onChange={(e) => setCountrySearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                  {countries
                    .filter(country => 
                      country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
                      country.dialCode.includes(countrySearchQuery)
                    )
                    .map((country) => (
                      <MenuItem
                        key={country.code}
                        onClick={() => {
                          setSelectedCountry(country)
                          setNewAdmin({ ...newAdmin, phone: country.dialCode + phoneLocal })
                          setCountryMenuAnchor(null)
                          setCountrySearchQuery('')
                        }}
                        selected={country.code === selectedCountry.code}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <span style={{ fontSize: '1.5rem' }}>{country.flag}</span>
                        </ListItemIcon>
                        <ListItemText 
                          primary={country.name}
                          secondary={country.dialCode}
                        />
                      </MenuItem>
                    ))}
                </Menu>

                {/* Phone Number Input */}
                <TextField
                  fullWidth
                  placeholder="779123456"
                  value={phoneLocal}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '')
                    setPhoneLocal(value)
                    setNewAdmin({ ...newAdmin, phone: selectedCountry.dialCode + value })
                  }}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                required
                helperText="Minimum 8 characters"
              />
            </Grid>
            {user?.role === 'superadmin' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Centre</InputLabel>
                  <Select
                    value={newAdmin.centre_id}
                    label="Centre"
                    onChange={(e) => setNewAdmin({ ...newAdmin, centre_id: e.target.value })}
                  >
                    {centres.map((centre) => (
                      <MenuItem key={centre.id} value={centre.id}>
                        {centre.code} - {centre.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateAdminDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateAdmin}
            variant="contained"
            color="primary"
            disabled={createLoading || !newAdmin.email || !newAdmin.first_name || !newAdmin.last_name || !newAdmin.phone || !newAdmin.password || (user?.role === 'superadmin' && !newAdmin.centre_id)}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Super Admin Dialog */}
      <Dialog open={createSuperAdminDialog} onClose={() => setCreateSuperAdminDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Super Admin</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newAdmin.first_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newAdmin.last_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Phone Number *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={(e) => setCountryMenuAnchor(e.currentTarget)}
                  sx={{ 
                    minWidth: 140, 
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    color: 'text.primary',
                    borderColor: 'divider'
                  }}
                  endIcon={<KeyboardArrowDown />}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>{selectedCountry.flag}</span>
                    <span>{selectedCountry.dialCode}</span>
                  </Box>
                </Button>
                
                <Menu
                  anchorEl={countryMenuAnchor}
                  open={Boolean(countryMenuAnchor)}
                  onClose={() => {
                    setCountryMenuAnchor(null)
                    setCountrySearchQuery('')
                  }}
                  PaperProps={{
                    sx: { width: 320, maxHeight: 400 }
                  }}
                >
                  <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search country or code..."
                      value={countrySearchQuery}
                      onChange={(e) => setCountrySearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Box>
                  {countries
                    .filter(country => 
                      country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) ||
                      country.dialCode.includes(countrySearchQuery)
                    )
                    .map((country) => (
                      <MenuItem
                        key={country.code}
                        onClick={() => {
                          setSelectedCountry(country)
                          setNewAdmin({ ...newAdmin, phone: country.dialCode + phoneLocal })
                          setCountryMenuAnchor(null)
                          setCountrySearchQuery('')
                        }}
                        selected={country.code === selectedCountry.code}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <span style={{ fontSize: '1.5rem' }}>{country.flag}</span>
                        </ListItemIcon>
                        <ListItemText 
                          primary={country.name}
                          secondary={country.dialCode}
                        />
                      </MenuItem>
                    ))}
                </Menu>

                <TextField
                  fullWidth
                  placeholder="779123456"
                  value={phoneLocal}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '')
                    setPhoneLocal(value)
                    setNewAdmin({ ...newAdmin, phone: selectedCountry.dialCode + value })
                  }}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                required
                helperText="Minimum 8 characters"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateSuperAdminDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateSuperAdmin}
            variant="contained"
            color="primary"
            disabled={createLoading || !newAdmin.email || !newAdmin.first_name || !newAdmin.last_name || !newAdmin.phone || !newAdmin.password}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Create Super Admin'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  )
}

export default Users
